import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Section = ({ number, title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4">
      <span className="text-[rgb(197,155,47)]">{number}.</span> {title}
    </h2>
    <div className="space-y-3 text-[rgb(218,218,218)] leading-relaxed text-[15px]">{children}</div>
  </div>
);

const TableRow = ({ label, point, purpose }) => (
  <tr className="border-b border-[rgba(255,255,255,0.07)]">
    <td className="py-3 pr-6 text-[rgb(197,155,47)] font-medium whitespace-nowrap">{label}</td>
    <td className="py-3 pr-6 text-[rgb(218,218,218)]">{point}</td>
    <td className="py-3 text-[rgb(161,161,170)] text-sm">{purpose}</td>
  </tr>
);

const Li = ({ children }) => (
  <li className="flex items-start space-x-2">
    <span className="text-[rgb(197,155,47)] mt-1 flex-shrink-0">•</span>
    <span>{children}</span>
  </li>
);

const PrivacyPolicy = () => {
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[rgb(17,17,19)] text-white">
      {/* Top bar */}
      <div className="bg-[rgb(26,28,30)] border-b border-[rgba(255,255,255,0.08)] px-6 py-4">
        <div className="max-w-[800px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-[rgb(197,155,47)] hover:text-[rgb(224,194,95)] transition-colors duration-200">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to Eshwald Ops</span>
          </Link>
          <div className="flex items-center space-x-2">
            <img src="/eshwald-icon.jpg" alt="Eshwald Ops" className="h-7 w-7 rounded-md object-cover" />
            <span className="font-bold text-sm"><span className="text-white">Eshwald</span><span className="text-[rgb(197,155,47)]"> Ops</span></span>
          </div>
        </div>
      </div>

      <div className="max-w-[800px] mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-xs font-semibold mb-4">
            <div className="w-6 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>LEGAL</span>
            <div className="w-6 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            Privacy <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Policy</span>
          </h1>
          <p className="text-[rgb(161,161,170)] text-sm">Eshwald Ops &nbsp;·&nbsp; Effective Date: March 11, 2026</p>
        </div>

        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-8">
          <p className="text-[rgb(218,218,218)] leading-relaxed mb-10 text-[15px]">
            Eshwald Ops ("we," "us," "our," or the "Business") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. By accessing or using our website and services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
          </p>

          <Section number="1" title="Information We Collect">
            <p className="font-semibold text-white mb-2">1.1 Information You Provide Directly</p>
            <p className="mb-4">We collect information that you voluntarily provide when you use our Services:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mb-6">
                <thead>
                  <tr className="border-b border-[rgba(197,155,47,0.3)]">
                    <th className="pb-3 pr-6 text-left text-[rgb(197,155,47)] font-semibold">Data Type</th>
                    <th className="pb-3 pr-6 text-left text-[rgb(197,155,47)] font-semibold">Collection Point</th>
                    <th className="pb-3 text-left text-[rgb(197,155,47)] font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <TableRow label="Name" point="Contact Form" purpose="To identify and address you in communications" />
                  <TableRow label="Email Address" point="Contact Form" purpose="To respond to your inquiries and communicate with you" />
                  <TableRow label="Message Content" point="Contact Form" purpose="To understand and respond to your inquiry or request" />
                </tbody>
              </table>
            </div>
            <p className="font-semibold text-white mb-2">1.2 Information Collected at Later Stages</p>
            <p className="mb-4">If you choose to enter into a partnership arrangement with us, additional personal details may be collected off-site for the purpose of contract execution and business operations.</p>
            <p className="font-semibold text-white mb-2">1.3 Automatically Collected Information</p>
            <p className="mb-2">When you visit our website, we may automatically collect:</p>
            <ul className="space-y-1">
              <Li>Browser type and version</Li>
              <Li>Operating system</Li>
              <Li>IP address (which may be anonymized)</Li>
              <Li>Pages visited and time spent on pages</Li>
              <Li>Referring website addresses</Li>
              <Li>Date and time of visits</Li>
            </ul>
          </Section>

          <Section number="2" title="How We Use Your Information">
            <ul className="space-y-2">
              <Li><strong className="text-white">Communication:</strong> To respond to inquiries and communicate about our services</Li>
              <Li><strong className="text-white">Service Delivery:</strong> To evaluate potential partnership opportunities</Li>
              <Li><strong className="text-white">Website Improvement:</strong> To analyze usage patterns and improve functionality</Li>
              <Li><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws and regulations</Li>
              <Li><strong className="text-white">Business Operations:</strong> To maintain records and conduct ordinary business operations</Li>
            </ul>
          </Section>

          <Section number="3" title="Data Sharing Practices">
            <p className="mb-3">We do not sell, trade, or rent your personal information to third parties. We may share your information only in these limited circumstances:</p>
            <ul className="space-y-2">
              <Li><strong className="text-white">Service Providers:</strong> Trusted third-party service providers who assist in operating our website, subject to confidentiality obligations</Li>
              <Li><strong className="text-white">Legal Requirements:</strong> If required by law or in response to valid requests by public authorities</Li>
              <Li><strong className="text-white">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</Li>
              <Li><strong className="text-white">Consent:</strong> With your explicit consent or at your direction</Li>
            </ul>
          </Section>

          <Section number="4" title="Cookies and Tracking Technologies">
            <p className="mb-3">Our website may use cookies and similar tracking technologies to enhance your browsing experience.</p>
            <p className="font-semibold text-white mb-2">4.1 Types of Cookies We May Use</p>
            <ul className="space-y-1 mb-4">
              <Li><strong className="text-white">Essential Cookies:</strong> Necessary for the website to function properly</Li>
              <Li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our website</Li>
              <Li><strong className="text-white">Preference Cookies:</strong> Enable the website to remember your preferences</Li>
            </ul>
            <p className="font-semibold text-white mb-2">4.2 Managing Cookies</p>
            <p className="mb-3">Most web browsers allow you to control cookies through their settings. Disabling cookies may affect certain functionality of our website.</p>
            <p className="font-semibold text-white mb-2">4.3 Third-Party Analytics</p>
            <p>We may use third-party analytics services (such as Google Analytics) to collect and analyze website usage information.</p>
          </Section>

          <Section number="5" title="Data Retention">
            <ul className="space-y-2">
              <Li><strong className="text-white">Contact Form Submissions:</strong> Retained for a reasonable period to respond to inquiries, typically no longer than 3 years unless an ongoing business relationship exists</Li>
              <Li><strong className="text-white">Analytics Data:</strong> Retained in accordance with our analytics provider's settings, typically in anonymized or aggregated form</Li>
              <Li><strong className="text-white">Legal Requirements:</strong> We may retain certain information for longer periods as required by applicable laws</Li>
            </ul>
          </Section>

          <Section number="6" title="Your Rights">
            <p className="mb-3">Depending on your location and applicable laws, you may have certain rights regarding your personal information:</p>
            <ul className="space-y-2">
              <Li><strong className="text-white">Access:</strong> The right to request a copy of the personal information we hold about you</Li>
              <Li><strong className="text-white">Correction:</strong> The right to request correction of inaccurate or incomplete information</Li>
              <Li><strong className="text-white">Deletion:</strong> The right to request deletion of your personal information, subject to certain exceptions</Li>
              <Li><strong className="text-white">Objection:</strong> The right to object to the processing of your personal information</Li>
              <Li><strong className="text-white">Portability:</strong> The right to request transfer of your personal information to another party</Li>
              <Li><strong className="text-white">Withdrawal of Consent:</strong> Where processing is based on consent, the right to withdraw that consent at any time</Li>
            </ul>
          </Section>

          <Section number="7" title="Security Measures">
            <p className="mb-3">We implement appropriate technical and organizational security measures to protect your personal information:</p>
            <ul className="space-y-1">
              <Li>Secure data transmission using encryption technologies (SSL/TLS)</Li>
              <Li>Limited access to personal information on a need-to-know basis</Li>
              <Li>Regular review of our data collection, storage, and processing practices</Li>
              <Li>Physical and electronic safeguards to protect against unauthorized access</Li>
            </ul>
          </Section>

          <Section number="8" title="Third-Party Links">
            <p>Our website may contain links to third-party websites or services. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites. We encourage you to review the privacy policies of any third-party websites you visit.</p>
          </Section>

          <Section number="9" title="Contact Information">
            <p className="mb-2">If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 inline-block">
              <p className="font-semibold text-white">Eshwald Ops</p>
              <a href="mailto:eshwaldsystems@gmail.com" className="text-[rgb(197,155,47)] hover:underline">eshwaldsystems@gmail.com</a>
            </div>
          </Section>

          <Section number="10" title="Policy Updates">
            <p>We may update this Privacy Policy from time to time. When we make changes, we will update the "Effective Date" at the top of this policy. Your continued use of our Services after any changes constitutes your acceptance of such changes.</p>
          </Section>

          <Section number="11" title="Children's Privacy">
            <p>Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.</p>
          </Section>

          <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.08)] text-[rgb(161,161,170)] text-sm">
            <p>© 2026 Eshwald Ops. All rights reserved. Last updated: March 11, 2026.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
